import { z } from 'zod';

import { entityIdSchema, nonEmptyTextSchema, utcDateTimeSchema } from './primitives';

export const identityStatusSchema = z.enum(['pending', 'active', 'locked', 'retired']);
export const identitySchema = z
  .object({
    identityId: entityIdSchema,
    authenticationSubject: nonEmptyTextSchema.max(500),
    displayName: nonEmptyTextSchema.max(100),
    email: z.email().nullable(),
    employeeNumber: z.string().trim().min(1).max(100).nullable(),
    status: identityStatusSchema,
    mfaCapable: z.boolean(),
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
  })
  .strict();

export const tenantStatusSchema = z.enum(['active', 'suspended']);
export const tenantSchema = z
  .object({
    tenantId: entityIdSchema,
    legalName: nonEmptyTextSchema.max(200),
    status: tenantStatusSchema,
    createdAt: utcDateTimeSchema,
  })
  .strict();

export const resourceScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('tenant'), tenantId: entityIdSchema }).strict(),
  z.object({ kind: z.literal('site'), tenantId: entityIdSchema, siteId: entityIdSchema }).strict(),
  z
    .object({
      kind: z.literal('contractor'),
      tenantId: entityIdSchema,
      siteId: entityIdSchema,
      contractorId: entityIdSchema,
    })
    .strict(),
]);

export const personaSchema = z.enum([
  'system-admin',
  'hq-safety-admin',
  'hq-staff',
  'hq-auditor',
  'site-manager',
  'safety-manager',
  'site-staff',
  'safety-staff',
  'partner-admin',
]);

export const dutyCapabilitySchema = z.enum([
  'tenant:manage',
  'membership:manage',
  'site:manage',
  'document:read',
  'document:write',
  'document:submit',
  'document:correct-approved',
  'approval:read',
  'approval:read-all',
  'approval:submit',
  'approval:decide',
  'psm:read',
  'psm:submit',
  'psm:assign',
  'psm:assign-safety',
  'psm:assign-facilities',
  'inspection:create',
  'victim-registry:read',
  'victim-registry:author',
  'victim-registry:review',
  'victim-registry:track',
  'victim-registry:close',
  'victim-registry:delete',
  'msds:read',
  'msds:submit',
  'msds:review',
  'msds:legal-finalize',
  'msds:export',
  'msds:archive',
  'education:read',
  'education:create',
  'education:edit',
  'education:delete',
  'education:submit',
  'education:review',
  'equipment-dispatch:request',
  'equipment-dispatch:control',
  'taxonomy:approve',
  'finding:issue',
  'finding:assign',
  'finding:track',
  'finding:action-perform',
  'finding:verify',
  'finding:close',
  'records:enterprise-read',
  'audit:read',
  'feedback:submit',
  'feedback:triage',
  // 현장 위험요인 제안 (안전신문고). Separate from `feedback:*` on purpose: reporting a plant
  // hazard is open to everyone on site including 협력업체, while reading and triaging one belongs
  // to 안전관리자. `feedback:submit` / `feedback:triage` describe a different audience for a
  // different record and cannot express that split.
  'hazard-report:submit',
  'hazard-report:triage',
  'self-heal:approve',
]);

export const membershipStatusSchema = z.enum([
  'invited',
  'pending-approval',
  'active',
  'locked',
  'retired',
]);

const effectiveMembershipShape = {
  status: membershipStatusSchema,
  validFrom: utcDateTimeSchema,
  validUntil: utcDateTimeSchema.nullable(),
  grantedBy: entityIdSchema,
};

function validateMembershipPeriod(
  membership: { validFrom: string; validUntil: string | null },
  context: z.RefinementCtx,
): void {
  if (
    membership.validUntil !== null &&
    Date.parse(membership.validUntil) <= Date.parse(membership.validFrom)
  ) {
    context.addIssue({
      code: 'custom',
      path: ['validUntil'],
      message: 'validUntil must be after validFrom',
    });
  }
}

export const tenantMembershipSchema = z
  .object({
    tenantMembershipId: entityIdSchema,
    tenantId: entityIdSchema,
    identityId: entityIdSchema,
    personas: z.array(z.enum(['system-admin', 'hq-safety-admin', 'hq-staff', 'hq-auditor'])),
    ...effectiveMembershipShape,
  })
  .strict()
  .superRefine(validateMembershipPeriod);

export const siteMembershipSchema = z
  .object({
    siteMembershipId: entityIdSchema,
    tenantMembershipId: entityIdSchema,
    tenantId: entityIdSchema,
    siteId: entityIdSchema,
    identityId: entityIdSchema,
    contractorId: entityIdSchema.nullable(),
    personas: z
      .array(
        z.enum(['site-manager', 'safety-manager', 'site-staff', 'safety-staff', 'partner-admin']),
      )
      .min(1),
    ...effectiveMembershipShape,
  })
  .strict()
  .superRefine((membership, context) => {
    validateMembershipPeriod(membership, context);
    const isPartner = membership.personas.includes('partner-admin');
    if (isPartner !== (membership.contractorId !== null)) {
      context.addIssue({
        code: 'custom',
        path: ['contractorId'],
        message:
          'Partner memberships require a contractor and non-partner memberships cannot use one',
      });
    }
    if (isPartner && membership.personas.length > 1) {
      context.addIssue({
        code: 'custom',
        path: ['personas'],
        message: 'Partner and principal-company personas cannot share one site membership',
      });
    }
  });

export const authorizationContextSchema = z
  .object({
    identity: identitySchema,
    tenantMemberships: z.array(tenantMembershipSchema),
    siteMemberships: z.array(siteMembershipSchema),
  })
  .strict()
  .superRefine((authorization, context) => {
    authorization.tenantMemberships.forEach((membership, index) => {
      if (membership.identityId !== authorization.identity.identityId) {
        context.addIssue({
          code: 'custom',
          path: ['tenantMemberships', index, 'identityId'],
          message: 'Tenant membership belongs to another identity',
        });
      }
    });
    authorization.siteMemberships.forEach((membership, index) => {
      if (membership.identityId !== authorization.identity.identityId) {
        context.addIssue({
          code: 'custom',
          path: ['siteMemberships', index, 'identityId'],
          message: 'Site membership belongs to another identity',
        });
      }
      const parent = authorization.tenantMemberships.find(
        (candidate) => candidate.tenantMembershipId === membership.tenantMembershipId,
      );
      if (!parent || parent.tenantId !== membership.tenantId) {
        context.addIssue({
          code: 'custom',
          path: ['siteMemberships', index, 'tenantMembershipId'],
          message: 'Site membership requires a matching parent tenant membership',
        });
      }
    });
  });

export type AuthorizationContext = z.infer<typeof authorizationContextSchema>;
export type DutyCapability = z.infer<typeof dutyCapabilitySchema>;
export type Identity = z.infer<typeof identitySchema>;
export type Persona = z.infer<typeof personaSchema>;
export type ResourceScope = z.infer<typeof resourceScopeSchema>;
export type SiteMembership = z.infer<typeof siteMembershipSchema>;
export type Tenant = z.infer<typeof tenantSchema>;
export type TenantMembership = z.infer<typeof tenantMembershipSchema>;

export const personaCapabilities = {
  'system-admin': dutyCapabilitySchema.options,
  'hq-safety-admin': [
    'membership:manage',
    'site:manage',
    'document:read',
    'document:write',
    'document:submit',
    'approval:read',
    'approval:read-all',
    'approval:submit',
    'approval:decide',
    'psm:read',
    'psm:submit',
    'psm:assign',
    'psm:assign-safety',
    'psm:assign-facilities',
    'inspection:create',
    'education:read',
    'victim-registry:read',
    'victim-registry:author',
    'victim-registry:review',
    'victim-registry:track',
    'victim-registry:close',
    'victim-registry:delete',
    'msds:read',
    'msds:review',
    'msds:legal-finalize',
    'msds:export',
    'msds:archive',
    'taxonomy:approve',
    'finding:issue',
    'finding:assign',
    'finding:track',
    'finding:verify',
    'finding:close',
    'records:enterprise-read',
    'audit:read',
    'feedback:submit',
    'feedback:triage',
    'hazard-report:submit',
    'self-heal:approve',
  ],
  // Headquarters browsing adds explicit read capabilities, never site commands.
  'hq-staff': ['document:read', 'approval:read', 'psm:read', 'records:enterprise-read', 'education:read', 'victim-registry:read', 'msds:read'],
  // Backward-compatible read-only alias. New assignments use `hq-staff`.
  'hq-auditor': [
    'education:read',
    'victim-registry:read',
    'msds:read',
    'document:read',
    'approval:read',
    'psm:read',
    'records:enterprise-read',
    'audit:read',
    'feedback:submit',
    'hazard-report:submit',
  ],
  'site-manager': [
    'site:manage',
    'document:read',
    'document:write',
    'document:submit',
    'approval:read',
    'approval:read-all',
    'approval:submit',
    'approval:decide',
    'psm:read',
    'psm:submit',
    'psm:assign',
    'psm:assign-safety',
    'psm:assign-facilities',
    'inspection:create',
    'education:read',
    'education:create',
    'education:edit',
    'education:submit',
    'education:review',
    'equipment-dispatch:request',
    'equipment-dispatch:control',
    'victim-registry:read',
    'victim-registry:author',
    'victim-registry:review',
    'victim-registry:track',
    'victim-registry:close',
    'victim-registry:delete',
    'msds:read',
    'msds:review',
    'msds:legal-finalize',
    'msds:export',
    'msds:archive',
    'taxonomy:approve',
    'finding:issue',
    'finding:assign',
    'finding:track',
    'finding:verify',
    'finding:close',
    'audit:read',
    'feedback:submit',
    'feedback:triage',
    'hazard-report:submit',
  ],
  'safety-manager': [
    'site:manage',
    'document:read',
    'document:write',
    'document:submit',
    'approval:read',
    'approval:read-all',
    'approval:submit',
    'approval:decide',
    'psm:read',
    'psm:submit',
    'psm:assign',
    'psm:assign-safety',
    'inspection:create',
    'education:read',
    'education:create',
    'education:edit',
    'education:delete',
    'education:submit',
    'education:review',
    'equipment-dispatch:request',
    'equipment-dispatch:control',
    'victim-registry:read',
    'victim-registry:author',
    'victim-registry:review',
    'victim-registry:track',
    'victim-registry:close',
    'victim-registry:delete',
    'msds:read',
    'msds:review',
    'msds:legal-finalize',
    'msds:export',
    'msds:archive',
    'finding:issue',
    'finding:assign',
    'finding:track',
    'finding:verify',
    'finding:close',
    'feedback:submit',
    'feedback:triage',
    'hazard-report:submit',
    'hazard-report:triage',
  ],
  // Factory general users receive only baseline participation/read rights.
  // Feature-specific write capabilities must be granted by a separate approved
  // policy rather than inheriting the safety-staff authoring bundle.
  'site-staff': [
    'document:read',
    'approval:read',
    'education:read',
    'equipment-dispatch:request',
    'feedback:submit',
    'hazard-report:submit',
  ],
  // Backward-compatible alias for existing safety-work appointments.
  'safety-staff': [
    'document:read',
    'document:write',
    'document:submit',
    'approval:read',
    'approval:submit',
    'psm:read',
    'psm:submit',
    'inspection:create',
    'education:read',
    'education:create',
    'education:edit',
    'education:submit',
    'equipment-dispatch:request',
    'finding:issue',
    'finding:track',
    'finding:verify',
    'feedback:submit',
    'hazard-report:submit',
  ],
  'partner-admin': [
    'document:read',
    'document:write',
    'document:submit',
    'approval:read',
    'approval:submit',
    'inspection:create',
    'education:read',
    'education:create',
    'education:edit',
    'education:submit',
    'equipment-dispatch:request',
    'victim-registry:read',
    'victim-registry:author',
    'msds:read',
    'msds:submit',
    'finding:track',
    'finding:action-perform',
    'hazard-report:submit',
    'feedback:submit',
  ],
} as const satisfies Record<Persona, readonly DutyCapability[]>;

const headquartersSiteReadCapabilities = new Set<DutyCapability>([
  'document:read',
  'approval:read',
  'approval:read-all',
  'psm:read',
  'records:enterprise-read',
  'audit:read',
  'education:read',
  'msds:read',
]);

export const headquartersFactoryReadCapabilities: readonly DutyCapability[] = [
  'document:read', 'approval:read', 'psm:read', 'records:enterprise-read',
  'education:read', 'victim-registry:read', 'msds:read',
];

/**
 * Capability applicability only: callers must first resolve the requested site
 * through active tenant site configuration and enforce its feature policy.
 * HQ business reads apply to configured factories without local duty grants;
 * all factory writes still require an effective site membership.
 */
export function tenantPersonaAppliesToTarget(
  persona: 'system-admin' | 'hq-safety-admin' | 'hq-staff' | 'hq-auditor',
  capability: DutyCapability,
  target: ResourceScope,
): boolean {
  if (target.kind === 'tenant') return personaCapabilities[persona].includes(capability as never);
  if (persona === 'system-admin') return personaCapabilities[persona].includes(capability as never);
  if (persona === 'hq-staff' || persona === 'hq-auditor')
    return headquartersFactoryReadCapabilities.includes(capability);
  if (persona === 'hq-safety-admin')
    return (
      (headquartersSiteReadCapabilities.has(capability) || capability === 'victim-registry:read') &&
      personaCapabilities[persona].includes(capability as never)
    );
  return false;
}

export function effectiveTenantCapabilitiesForTarget(
  personas: readonly unknown[],
  storedCapabilities: readonly unknown[],
  targetKind: ResourceScope['kind'],
): readonly DutyCapability[] {
  const target: ResourceScope =
    targetKind === 'tenant'
      ? { kind: 'tenant', tenantId: 'scope-check' }
      : targetKind === 'site'
        ? { kind: 'site', tenantId: 'scope-check', siteId: 'scope-check' }
        : {
            kind: 'contractor',
            tenantId: 'scope-check',
            siteId: 'scope-check',
            contractorId: 'scope-check',
          };
  const canonicalPersonas = personas.filter(
    (persona): persona is 'system-admin' | 'hq-safety-admin' | 'hq-staff' | 'hq-auditor' =>
      typeof persona === 'string' &&
      ['system-admin', 'hq-safety-admin', 'hq-staff', 'hq-auditor'].includes(persona),
  );
  const exactStored = (expected: readonly string[]) =>
    storedCapabilities.length === expected.length && expected.every((item) => storedCapabilities.includes(item));
  const legacyHeadquartersGeneral =
    canonicalPersonas.length === 1 &&
    ((canonicalPersonas[0] === 'hq-staff' &&
      (exactStored(['report.read']) || exactStored(['document:read']) ||
       exactStored(['document:read', 'approval:read', 'psm:read', 'records:enterprise-read']))) ||
     (canonicalPersonas[0] === 'hq-auditor' && exactStored([
       'document:read', 'approval:read', 'psm:read', 'records:enterprise-read',
       'audit:read', 'feedback:submit', 'hazard-report:submit',
     ])));
  const effectiveStoredCapabilities = legacyHeadquartersGeneral
    ? personaCapabilities[canonicalPersonas[0] as 'hq-staff' | 'hq-auditor']
    : storedCapabilities;
  return dutyCapabilitySchema.options.filter(
    (capability) =>
      effectiveStoredCapabilities.includes(capability) &&
      canonicalPersonas.some((persona) =>
        tenantPersonaAppliesToTarget(persona, capability, target),
      ),
  );
}

export function scopeContains(grant: ResourceScope, target: ResourceScope): boolean {
  if (grant.tenantId !== target.tenantId) return false;
  if (grant.kind === 'tenant') return true;
  if (grant.kind === 'site') return target.kind !== 'tenant' && grant.siteId === target.siteId;
  return (
    target.kind === 'contractor' &&
    grant.siteId === target.siteId &&
    grant.contractorId === target.contractorId
  );
}

function membershipIsEffective(
  membership: {
    status: z.infer<typeof membershipStatusSchema>;
    validFrom: string;
    validUntil: string | null;
  },
  at: Date,
): boolean {
  const instant = at.getTime();
  return (
    membership.status === 'active' &&
    Date.parse(membership.validFrom) <= instant &&
    (membership.validUntil === null || instant < Date.parse(membership.validUntil))
  );
}

export function hasCapability(
  authorization: AuthorizationContext,
  capability: DutyCapability,
  target: ResourceScope,
  at: Date = new Date(),
): boolean {
  if (authorization.identity.status !== 'active') return false;

  const tenantGrant = authorization.tenantMemberships.some(
    (membership) =>
      membership.tenantId === target.tenantId &&
      membershipIsEffective(membership, at) &&
      membership.personas.some((persona) =>
        tenantPersonaAppliesToTarget(persona, capability, target),
      ),
  );
  if (tenantGrant) return true;

  return authorization.siteMemberships.some((membership) => {
    const parentTenantMembership = authorization.tenantMemberships.find(
      (candidate) => candidate.tenantMembershipId === membership.tenantMembershipId,
    );
    if (!parentTenantMembership || !membershipIsEffective(parentTenantMembership, at)) {
      return false;
    }

    const scope: ResourceScope = membership.contractorId
      ? {
          kind: 'contractor',
          tenantId: membership.tenantId,
          siteId: membership.siteId,
          contractorId: membership.contractorId,
        }
      : { kind: 'site', tenantId: membership.tenantId, siteId: membership.siteId };

    return (
      membershipIsEffective(membership, at) &&
      membership.personas.some((persona) =>
        personaCapabilities[persona].includes(capability as never),
      ) &&
      scopeContains(scope, target)
    );
  });
}
